import React, { useRef, useEffect, useState } from "react";

import checkIcon from "@assets/icons/check.svg?raw&inline";

export type StepStatus = "pending" | "completed";

// Component for the connecting line that dynamically adjusts
// Positioned relative to the circle itself (which has position: relative)
const ConnectingLine: React.FC<{ containerRef: React.RefObject<HTMLDivElement | null>; marginBottom: number }> = ({ 
  containerRef, 
  marginBottom 
}) => {
  const [lineHeight, setLineHeight] = useState(33 + marginBottom);

  useEffect(() => {
    const calculateLineHeight = () => {
      if (!containerRef.current) return;
      
      // Get the container's actual height
      const containerHeight = containerRef.current.offsetHeight;
      // Calculate from bottom of circle (22px) to bottom of container + margin
      // The line starts at 22px (bottom of circle) and should extend to bottom of item + margin
      const calculatedHeight = containerHeight - 22 + marginBottom;
      
      setLineHeight(Math.max(33 + marginBottom, calculatedHeight));
    };

    calculateLineHeight();
    
    // Recalculate on window resize
    window.addEventListener('resize', calculateLineHeight);
    
    // Use ResizeObserver to detect content size changes
    const resizeObserver = new ResizeObserver(() => {
      calculateLineHeight();
    });
    
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      window.removeEventListener('resize', calculateLineHeight);
      resizeObserver.disconnect();
    };
  }, [containerRef, marginBottom]);
  
  return (
    <div
      style={{
        position: "absolute",
        width: "2px",
        height: `${lineHeight}px`,
        top: "22px", // Start from bottom of circle (circle is 22px tall)
        left: "50%", // Center horizontally on the circle
        transform: "translateX(-50%)", // Center the 2px line on the circle center
        background: "#DFDFDF",
        zIndex: -1,
      }}
    />
  );
};

// Get circle styles based on status - using Bootstrap classes where possible
const getCircleStyles = (status: StepStatus, current: boolean): React.CSSProperties => {
  const baseStyles: React.CSSProperties = {
    fontSize: "10px",
    borderRadius: "100%",
    width: "22px",
    height: "22px",
    minWidth: "22px",
    boxSizing: "border-box",
  };

  if (status === "pending" && current) {
    // Current step styles
    return {
      ...baseStyles,
      backgroundColor: "#fff",
      border: "2px dashed #2C6ECA",
      color: "#212529",
    };
  } else if (status === "completed") {
    // Completed step styles
    return {
      ...baseStyles,
      backgroundColor: "#2B9644",
      color: "#fff",
    };
  } else {
    // Default/pending styles
    return {
      ...baseStyles,
      backgroundColor: "#EFEFEF",
      color: "#212529",
    };
  }
};

export const ProgressStepItem: React.FC<ProgressStepItemProps> = ({
  hasLine,
  hideNumber,
  index,
  label,
  description,
  status,
  current,
}) => {

  const itemTextClass = () => {
    let className = "form-label text-dark size-14 mb-0";

    if (hideNumber || current || status === "completed") {
      className += " font-weight-600";
    }

    return className;
  };

  const marginBottom = hideNumber ? 10 : 33;
  const containerRef = useRef<HTMLDivElement>(null);
  
  return (
    <div ref={containerRef} className="col-12 py-0" style={{ marginBottom }}>
      <div className="align-self-center position-relative">
        <div className="d-flex">
          {!hideNumber && (
            <span
              className="d-flex align-items-center justify-content-center fw-bold me-2 position-relative"
              style={getCircleStyles(status, current)}
            >
              {index + 1}
              {hasLine && <ConnectingLine containerRef={containerRef} marginBottom={marginBottom} />}
            </span>
          )}
          <div className="d-flex flex-column text-start">
            <span className={itemTextClass()}>{label}</span>
            {description && (
              <span className="form-label text-muted size-11 mb-0 text-start">
                {description}
              </span>
            )}
          </div>
          {status === "completed" && (
            <span
              className="d-flex align-items-center text-success ms-2"
              dangerouslySetInnerHTML={{ __html: checkIcon }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

interface ProgressStepItemProps {
  index: number;
  label: string;
  description?: string;
  status: StepStatus;
  current: boolean;
  hasLine: boolean;
  hideNumber: boolean;
}
